import { createRef, FC, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  BoxedTable,
  Header,
  IllustrationPaperMissing,
  Modal,
  useIsMobile,
} from '@bbl-digital/snorre'
import SearchBox from 'shared/components/SearchBox'
import {
  HeaderWrapper,
  ContentCSSOverride,
  ContentWrapper,
  HeaderCSSOverride,
  HorizontalPadding,
} from './styles'
import { getMessageRecipients } from 'shared/store/sendMessage/recipients/selectors'
import { mobileBreakpoint } from 'shared/constants/styles'
import SkeletonTable from 'shared/components/skeletons/SkeletonTable'
import NoResults from 'shared/components/NoResults'
import { FilterTypeEnum, IRecipientModel } from '../../models/Recipients'
import Pagination from 'shared/components/Pagination'
import { ICommonFilterModel } from 'shared/models/Common'
import {
  handleRecipientModal,
  onRecipientsClose,
  setRecipientsFilters,
} from 'shared/store/sendMessage/recipients'
import RecipientsListHeader from './RecipientsListHeader'
import RecipientListItem from './RecipientsListItem'
import RecipientsListActions from './RecipientsListActions'
import ScrollableDiv from 'shared/components/ScrollableDiv'
import RecipientsListFilters from './RecipientsListFilters'
import useInitRecipientsModal from '../../hooks/useInitRecipientsModal'
import { getTerminology } from 'shared/store/terminology/selectors'
import ReservedUsersComponent from 'shared/components/ReservedUsersComponent'

const RecipientsListModal: FC = () => {
  useInitRecipientsModal()

  const isMobile = useIsMobile()
  const dispatch = useDispatch()
  const previousDataLength = useRef(10)
  const scrolldivRef = createRef<HTMLDivElement>()
  const { data, loading, error, filters } = useSelector(getMessageRecipients)
  const { data: terminology } = useSelector(getTerminology)
  const { openState } = useSelector(getMessageRecipients)

  const isHabitants =
    filters?.filterType === FilterTypeEnum.Owners ||
    filters?.filterType === FilterTypeEnum.Tenants ||
    filters?.filterType === FilterTypeEnum.Cohabitant
  const isMetersOrObjects =
    filters.filterType === FilterTypeEnum.Objects ||
    filters.filterType === FilterTypeEnum.Meters
  const isOtherOrBoardContacts =
    filters.filterType === FilterTypeEnum.BoardContacts ||
    filters.filterType === FilterTypeEnum.OtherContacts
  const isBoardAndCommitee = filters.filterType === FilterTypeEnum.BoardMembers

  const getBoxTableColumns = (): string => {
    if (isMetersOrObjects)
      return openState === 'Message'
        ? '36px minmax(145px, 1fr) minmax(240px, 1.5fr) 30px'
        : '36px minmax(145px, 1fr) minmax(240px, 1.5fr) minmax(200px, 1.5fr) 30px'
    if (isOtherOrBoardContacts)
      return '36px minmax(145px, 1fr) minmax(100px, 1.5fr) minmax(100px, 1.5fr) minmax(240px, 1.5fr)'
    if (isBoardAndCommitee)
      return '36px minmax(140px, 1fr) minmax(275px, 1.5fr) minmax(200px, 1.5fr)'
    // Owners and tenants
    return openState === 'Message'
      ? '36px minmax(140px, 1fr) minmax(70px, 0.25fr) minmax(150px, 1.5fr) minmax(65px, 100px) minmax(65px, 100px)'
      : '36px minmax(140px, 1fr) minmax(170px, 1.5fr) minmax(70px, 0.25fr) minmax(150px, 1.5fr) minmax(65px, 100px) minmax(65px, 100px) '
  }

  useEffect(() => {
    previousDataLength.current = data?.result.length || 10
  }, [data?.result])

  const handleChange = (action: string) => (amount: number | string) =>
    dispatch(setRecipientsFilters({ [action]: amount }))

  const onClose = () => {
    dispatch(handleRecipientModal({ open: null, type: null }))
    dispatch(onRecipientsClose())
  }

  return (
    <Modal
      width="890px"
      hideCloseButton
      open
      disableScrollableContent
      targetRefs={[scrolldivRef]}
      disableScroll
      actionButton={<RecipientsListActions />}
      contentCss={ContentCSSOverride}
      headerCss={HeaderCSSOverride}
      onClose={onClose}
      header={
        <HeaderWrapper>
          <Header level={4}>Finn mottaker</Header>
          <SearchBox
            value={filters.searchText}
            info={`Søk etter ${
              isHabitants ? terminology.Eiere.toLowerCase() : 'navn'
            }, e-post eller telefonnummer`}
            placeholder="Søk i kontakter..."
            onDebounceChange={handleChange('searchText')}
          />
        </HeaderWrapper>
      }
    >
      <HorizontalPadding>
        {!isMobile && (
          <ReservedUsersComponent overrideMessage="= Reservert seg mot elektronisk kommunikasjon" />
        )}

        <RecipientsListFilters />
      </HorizontalPadding>

      <BoxedTable
        columns={getBoxTableColumns()}
        breakpoint={mobileBreakpoint + 'px'}
        type="compact"
        hasCheckbox
      >
        <RecipientsListHeader />

        <ScrollableDiv ref={scrolldivRef}>
          {(loading || !Array.isArray(data?.result)) && !error && (
            <SkeletonTable
              columns={6}
              rows={data?.result?.length || previousDataLength.current}
            />
          )}

          {!loading &&
            !error &&
            Array.isArray(data?.result) &&
            !data?.result.length && (
              <ContentWrapper>
                <NoResults
                  illustration={<IllustrationPaperMissing />}
                  hasSearch={Boolean(filters?.searchText)}
                  text="Ingen kontakter funnet"
                />
              </ContentWrapper>
            )}

          {!loading &&
            data?.result?.map((recipient: IRecipientModel) => (
              <RecipientListItem key={recipient.nameId} recipient={recipient} />
            ))}
        </ScrollableDiv>
      </BoxedTable>

      {Boolean(error) && (
        <ContentWrapper>
          <NoResults
            illustration={<IllustrationPaperMissing />}
            text="Det skjedde en feil ved uthenting av mottakere"
          />
        </ContentWrapper>
      )}

      <HorizontalPadding>
        <Pagination
          list={data}
          filters={filters as ICommonFilterModel}
          onTakeChange={handleChange('take')}
          onSkipChange={handleChange('skip')}
        />
      </HorizontalPadding>
    </Modal>
  )
}

export default RecipientsListModal
